import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { ArrowLeft, ArrowRight, PaperPlaneTilt, House } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t, isRtl } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(t('fp_err_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }}>
      <Link
        to="/"
        data-testid="auth-back-home"
        className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} z-10 inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all`}
      >
        <House size={16} weight="regular" />
        <span className="hidden sm:inline">{t('back_to_home')}</span>
      </Link>
      <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} z-10`}><LangSwitch /></div>
      <div className="w-full max-w-md animate-fade-in-up" data-testid="forgot-password-page">
        <div className="flex items-center gap-3 mb-8 justify-center"><Logo size="xl" /></div>
        <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          {sent ? (
            <div data-testid="reset-sent-message">
              <div className="w-14 h-14 rounded-full bg-[#01AF9B]/10 flex items-center justify-center mx-auto mb-4">
                <PaperPlaneTilt size={28} className="text-[#01AF9B]" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2 text-center">{t('fp_sent_title')}</h1>
              <p className="text-slate-400 text-sm text-center mb-6">{t('fp_sent_msg_a')}<span className="text-[#01AF9B]" dir="ltr">{email}</span>{t('fp_sent_msg_b')}</p>
              <Link to="/login" className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2" data-testid="back-to-login">
                <BackArrow size={18} /> {t('fp_back_login')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2">{t('fp_title')}</h1>
              <p className="text-slate-400 text-sm mb-6">{t('fp_subtitle')}</p>
              {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">{t('fp_email')}</label>
                  <input data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
                    placeholder={t('fp_email_ph')} dir="ltr" />
                </div>
                <button data-testid="forgot-submit" type="submit" disabled={loading}
                  className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('fp_submit')}
                </button>
              </form>
              <p className="text-center text-slate-500 text-sm mt-5">
                <Link to="/login" className="text-[#01AF9B] hover:text-[#01AF9B]/80 font-medium flex items-center justify-center gap-1">
                  <BackArrow size={14} /> {t('fp_back_login')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
